import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import qs from "query-string";
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from 'react-use';
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import DetailsPageHeader from './DetailsPageHeader';
import DataTable from '../tableComponents/Table';
import {
    pageSize,
    isSubset,
    generateGlobalFilters,
    optionalFiltersByTable,
    defaultHeaderFiltersByTable,
    globalFiltersRequired,
    generateTableFilters,
    getCarrierEventResponseConfig
} from '../../helpers';
import { filtersOnTime as defaultStoreFilters } from "../../constData/filtersOnTime";
import TrackNTraceHeader from '../trackNtrace/TrackNTraceHeader';
import { getTableData } from '../../redux/reducers/tableDataSlice';
import { optionalFilterPayloadCER } from '../../constData/filterRequestPayloadCER';

const CarrierEventResponse = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const tableNameCER = 'carrierEventResponse';
    const { carrierid: carrieridCER } = useParams();
    const { tableData = {}, isLoading: isLoadingCER } = useSelector(state => state?.loadDetails || {});
    const [searchParams, setSearchParams] = useSearchParams();

    // Local state that holds ALL filter values (default + URL params)
    const [search, setSearchCER] = React.useState(() => Object.fromEntries([...searchParams]));
    const eventType = searchParams.get('eventType');
    const stopEventType = searchParams.get('stopEventType');
    const isDropStop = eventType && (eventType === 'arrival_at_dropstop' || eventType === 'departure_from_dropstop');
    const tableNameWithEndpoint = isDropStop ? 'carrierEventResponseDrop' : 'carrierEventResponsePick';

    const stopEventTypeResponseRate = [
        'PICKUP_ARRIVAL_CARRIER_RESPONSE_RECEIVED',
        'PICKUP_DEPARTURE_CARRIER_RESPONSE_RECEIVED',
        'DELIVERY_ARRIVAL_CARRIER_RESPONSE_RECEIVED',
        'DELIVERY_DEPARTURE_CARRIER_RESPONSE_RECEIVED'
    ];

    const totalResponseRate = stopEventTypeResponseRate.includes(stopEventType);

    const [shipments, setShipmentsCER] = useState([]);
    const [loading, setLoadingCER] = useState(true);
    const [totalPages, setTotalPagesCER] = useState(0);
    const [totalRecords, setTotalRecordsCER] = useState(0);
    const [onPage, setOnPageCER] = useState(1);
    const [reqPayload, setReqPayloadCER] = useState({});
    // URL Params 
    useDeepCompareEffect(() => {
        let filterValues = {};
        const params = Object.fromEntries([...searchParams]);
        Object.keys(optionalFilterPayloadCER).forEach(d => {
            if (optionalFilterPayloadCER[d]) {
                filterValues[d] = JSON.stringify(optionalFilterPayloadCER[d]);
            }
        })
        setSearchParams(qs.stringify({
            ...filterValues,
            ...params,
        }));
        setSearchCER({
            ...filterValues,
            ...params,
        });
        setOnPageCER(1);
    }, [searchParams]);

    // API Params
    useDeepCompareEffect(() => {
        const controller = new AbortController();

        const globalFilters = generateGlobalFilters(globalFiltersRequired, search);
        const { tableFilters, definedValues } = generateTableFilters(optionalFiltersByTable[tableNameCER], search, defaultStoreFilters);
        const commonPayload = defaultHeaderFiltersByTable[tableNameCER];

        const reqBody = {
            ...globalFilters,
            ...commonPayload,
            ...tableFilters,
            carrierCodeList: [carrieridCER],
            deliveryActualArrivalDatetimeFinalDestTZ: globalFilters.deliveryActualArrivalDatetimeFinalDestTZ,
        };

        if (isSubset(Object.keys(search), Object.keys(definedValues))) {
            setReqPayloadCER(reqBody);
            dispatch(getTableData({
                reqBody,
                pageSize: pageSize,
                pageNum: onPage,
                tableName: tableNameWithEndpoint, // dynamic table name to select correct endpoint
                controller
            }));
        }
        return () => {
            controller.abort();
        };
    }, [search, onPage, carrieridCER])


    useEffect(() => {
        setShipmentsCER(tableData?.shipments || []);
        setLoadingCER(isLoadingCER);
        setTotalPagesCER(tableData?.totalPages || 0);
        setTotalRecordsCER(tableData?.totalRecords || 0);
    }, [tableData, isLoadingCER]);

    return (
        <Grid container className='pageContainer'>
            <DetailsPageHeader
                buttonTitle={totalResponseRate ? t('total_response_rate') : t('on_time_response_rate')}
                pageName={tableNameWithEndpoint}
                enableLanes={false}
            />
            <Grid item xs={12} className='actionSectionPart'>
                <TrackNTraceHeader
                    refreshBtn={false}
                    tab={'performance'}
                    tableName={tableNameWithEndpoint}
                    reqPayload={reqPayload}
                    showSearchOnly={true}
                    csvLabelsProps={getCarrierEventResponseConfig(eventType, true)} // csvMapping = true
                />
            </Grid>
            <Grid item xs={12}>
                <DataTable
                    tableName={tableNameWithEndpoint}
                    rowData={shipments}
                    columnOrder={getCarrierEventResponseConfig(eventType, false)} // csvMapping = false
                    loading={loading}
                    size={pageSize}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPage={onPage}
                    setOnPage={setOnPageCER}
                />
            </Grid>
        </Grid>
    )
}

export default CarrierEventResponse